import * as THREE from "three";
import type { SkinKind } from "@highmarch/domain";
import { FlagEmblem } from "../flagEmblem";
import { RoadSkin } from "./roadSkin";
import { SkinMaterials } from "./skinMaterial";
import { SKIN_LOOKS, lookOf } from "./looks";
import { SkinKits } from "./kit/runtime";
import { PET_KITS, ROAD_KITS, TROOP_KITS } from "./kits";
import { petModelName, petStandIn } from "./kits/pets";
import { lib } from "../views/common";

/** What the player wears, as the renderer needs it (features/cosmetics.ts fills it from @highmarch/app Cosmetics). */
export interface WornSet {
  /** Tower id → skin id; a tower not listed wears its stock look. */
  readonly towers: ReadonlyMap<string, string>;
  readonly fortress: string | null;
  readonly troop: string | null;
  readonly emblem: string | null;
  readonly road: string | null;
  /** MP-23: the title frame (ui/titleFrame.ts) and the kill effect (views/killFx.ts); absent = none. */
  readonly frame?: string | null;
  readonly kill?: string | null;
  /** Pet id → the skin it wears (docs/PETS.md); a pet not listed wears its stock look. */
  readonly pets?: ReadonlyMap<string, string>;
  /** The pet that comes into battles (and naps in the hub), or null: none had yet, or the Commander goes without. */
  readonly pet?: string | null;
  /** Pets v3: every pet had, in roster order (they all wander the hub's lawn). */
  readonly petsOwned?: readonly string[];
}

const NONE: WornSet = { towers: new Map(), fortress: null, troop: null, emblem: null, road: null, frame: null, kill: null, pets: new Map(), pet: null };

/** A dressed tower model still (possibly) on screen, for a live re-dress when the worn skin changes. */
interface Dressed { readonly root: THREE.Object3D; readonly archId: string; readonly isBase: boolean; readonly at: number }

/**
 * The worn skins on the render side (FINISH_PLAN WP8): one place the views ask what to wear — the tower and fortress
 * models (views/towerModel.ts calls `dressTower`), the troops (views/troops.ts `dressTroop`), the pennant emblem
 * (flagEmblem.ts) and the road (roadSkin.ts). `set` applies a change at once: emblem and road in place, towers and
 * the fortress re-dressed where they stand (the hub's island), and a fortress MODEL change (the Dragonlord) asks the
 * hub to rebuild through `onFortressModel`, since that is another GLB, not a material.
 *
 * Presentation only: the sim never reads any of this (app/cosmetics.ts; the golden test).
 */
export class WornSkins {
  private static worn: WornSet = NONE;
  private static readonly dressed = new Set<Dressed>();
  /** A dressed model that has not joined a scene this long after it was dressed is taken for gone (a level torn down). */
  static readonly StaleMs = 5000;
  /** Set by the hub flow: rebuild the hub when the fortress's model, or a worn tower kit's own model, changes. */
  static onFortressModel: (() => void) | null = null;

  static get(): WornSet { return WornSkins.worn; }

  /** The worn skin of a kind (a tower id for "tower", a pet id for "pet"), or null for stock. */
  static of(kind: SkinKind, towerId: string | null = null): string | null {
    const w = WornSkins.worn;
    if (kind === "pet") return w.pets?.get(towerId ?? "") ?? null;
    return kind === "tower" ? w.towers.get(towerId ?? "") ?? null : w[kind] ?? null;
  }

  /** The pet that comes along (into battles; it wears the gold ring on the hub's lawn), or null. */
  static pet(): string | null { return WornSkins.worn.pet ?? null; }
  /** Every pet had (Pets v3: they all wander the hub's lawn), in roster order. */
  static petsOwned(): readonly string[] { return WornSkins.worn.petsOwned ?? (WornSkins.worn.pet ? [WornSkins.worn.pet] : []); }

  /** The worn fortress skin's model suffix (`base_t<n>__<model>`), or null for a stock or material-only keep. */
  static fortressModel(): string | null { return WornSkins.modelOf(WornSkins.worn.fortress); }
  static modelOf(fortress: string | null): string | null { return (fortress ? SKIN_LOOKS[fortress]?.model : undefined) ?? null; }

  /** The worn tower skin's own model suffix for tower `archId` (big kits: TowerKit.model), or null. */
  static towerModel(archId: string): string | null { return WornSkins.towerModelOf(WornSkins.worn.towers.get(archId) ?? null); }
  /** A tower skin's own model suffix (`tower_<id>_t<n>__<model>`), or null for a kit on the stock model. */
  static towerModelOf(skin: string | null): string | null { return SkinKits.towerKit(skin)?.model ?? null; }

  /** Whether a worn tower skin swaps the model (its own model ships): the hub then rebuilds rather than re-dresses. */
  static swapsTowerModel(archId: string, skin: string | null): boolean {
    const m = WornSkins.towerModelOf(skin);
    return m !== null && (lib()?.ships(`tower_${archId}_t1__${m}`) ?? false);
  }

  /** Every worn tower kit's own (shipped) model, as one string: a change asks the hub to rebuild (another GLB). */
  private static towerModels(w: WornSet): string {
    return [...w.towers].filter(([t, s]) => WornSkins.swapsTowerModel(t, s)).map(([t, s]) => `${t}:${WornSkins.towerModelOf(s)}`).sort().join(",");
  }

  static set(next: WornSet): void {
    const before = WornSkins.fortressModel(), towersBefore = WornSkins.towerModels(WornSkins.worn);
    WornSkins.worn = next;
    if (typeof document !== "undefined") FlagEmblem.setEmblem(lookOf("emblem", next.emblem).emblem ?? null);
    RoadSkin.set((next.road ? ROAD_KITS[next.road]?.tint : undefined) ?? lookOf("road", next.road).road ?? null, (next.road ? ROAD_KITS[next.road]?.surface : undefined) ?? null);
    WornSkins.refresh();
    if (WornSkins.fortressModel() !== before || WornSkins.towerModels(next) !== towersBefore) WornSkins.onFortressModel?.();
  }

  /**
   * Dresses a freshly built tower or fortress model in its worn skin (or `skin`: the picker's preview), and keeps it
   * for a live re-dress. The Dragonlord's own model needs no material skin; a fortress skin without a model is one.
   */
  static dressTower(root: THREE.Object3D, archId: string, isBase: boolean, skin?: string | null): void {
    const id = skin !== undefined ? skin : isBase ? WornSkins.worn.fortress : WornSkins.worn.towers.get(archId) ?? null;
    // a tower skin with a kit (render/skins/kits/) wears its material set and attachments; one without, its legacy tint
    const kit = isBase ? null : SkinKits.towerKit(id);
    const body = !kit && id ? SKIN_LOOKS[id]?.body ?? null : null;
    // the two share the remembered stock material: take the other one off first, then put this one on
    if (kit) { SkinMaterials.dress(root, null, null); SkinKits.dressTower(root, archId, kit); }
    else { if (!isBase) SkinKits.dressTower(root, archId, null); SkinMaterials.dress(root, body ? id : null, body); }
    if (skin === undefined) {
      WornSkins.prune();
      WornSkins.dressed.add({ root, archId, isBase, at: performance.now() });
    }
  }

  /** A troop body before its unit look clones the materials (views/troops.ts): the worn troop skin. */
  static dressTroop(root: THREE.Object3D, skin?: string | null): void {
    const id = skin !== undefined ? skin : WornSkins.worn.troop;
    // a troop skin whose own soldier ships is that model (troopModel): nothing to tint
    const own = id !== null && SkinKits.troopModel(id) !== SkinKits.troopModel(null);
    const body = !own && id ? (TROOP_KITS[id]?.fallback ?? SKIN_LOOKS[id]?.body ?? null) : null;
    SkinMaterials.dress(root, body ? id : null, body);
  }

  /** The soldier model the worn troop skin (or `skin`) uses: `troop_soldier__<skin>` when it ships, else the stock one. */
  static troopModel(skin?: string | null): string { return SkinKits.troopModel(skin !== undefined ? skin : WornSkins.worn.troop); }

  /** The model pet `petId` wears in its worn skin (or `skin`): `pet_<pet>__<skin>` when it ships, else its stock one. */
  static petModel(petId: string, skin?: string | null): string { return SkinKits.petModel(petId, skin !== undefined ? skin : WornSkins.of("pet", petId)); }

  /**
   * A pet body before anything clones its materials: drawn on its coat's own model, nothing; on its stock model, the
   * coat's tint; on the stand-in pet's model (its own not shipped yet), the coat's tint or else the pet's own colours.
   */
  static dressPet(root: THREE.Object3D, petId: string, skin?: string | null): void {
    const id = skin !== undefined ? skin : WornSkins.of("pet", petId);
    const kit = id ? PET_KITS[id] : undefined, coat = kit && kit.pet === petId ? kit : undefined;
    const drawn = SkinKits.petModel(petId, id);
    if (coat && drawn === coat.model) { SkinMaterials.dress(root, null, null); return; }
    if (coat) { SkinMaterials.dress(root, coat.id, coat.fallback); return; }
    const stand = drawn !== petModelName(petId) ? petStandIn(petId) : null;
    SkinMaterials.dress(root, stand ? `standin:${petId}` : null, stand);
  }

  /** Re-dresses every kept model that is still in a scene (a skin change, or a kit GLB that just arrived). */
  static refresh(): void {
    WornSkins.prune();
    for (const d of WornSkins.dressed) if (inScene(d.root)) WornSkins.dressTower(d.root, d.archId, d.isBase, d.isBase ? WornSkins.worn.fortress : WornSkins.worn.towers.get(d.archId) ?? null);
  }

  private static prune(): void {
    const now = performance.now();
    for (const d of WornSkins.dressed) if (now - d.at > WornSkins.StaleMs && !inScene(d.root)) WornSkins.dressed.delete(d);
  }
}

function inScene(o: THREE.Object3D): boolean {
  let p: THREE.Object3D | null = o;
  while (p.parent) p = p.parent;
  return (p as THREE.Scene).isScene === true;
}

SkinKits.onKitLoaded = () => WornSkins.refresh();
